import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import envConfig from '../configs'
import { EFirebasePermission } from 'constants/notification'

// Firebase configuration interface for type safety
interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
}

// Validate Firebase configuration
const validateFirebaseConfig = (): boolean => {
  const requiredVars = [
    'firebase_api_key',
    'firebase_auth_domain',
    'firebase_project_id',
    'firebase_messaging_sender_id',
    'firebase_app_id',
    'firebase_vapid_key'
  ] as const

  const missing = requiredVars.filter(key => !envConfig[key])
  if (missing.length > 0) {
    console.error('Missing Firebase environment variables:', missing)
    return false
  }

  // Validate VAPID key format (should be Web Push format, starts with 'B' and is base64url-encoded)
  const vapidKey = envConfig.firebase_vapid_key
  if (vapidKey) {
    // Firebase VAPID keys are in Web Push format (base64url-encoded, usually start with 'B')
    // They should be 87-88 characters long and use URL-safe base64 characters
    const isValidLength = vapidKey.length >= 80 && vapidKey.length <= 90
    const hasValidFormat = /^[A-Za-z0-9_-]+$/.test(vapidKey)

    if (!isValidLength || !hasValidFormat) {
      console.error('Invalid VAPID key format:', {
        length: vapidKey.length,
        expectedLength: '80-90 characters',
        validFormat: hasValidFormat
      })
      return false
    }

    console.log('VAPID key validation passed:', {
      length: vapidKey.length,
      prefix: vapidKey.substring(0, 6) + '...',
      isValidFormat: true
    })
  } else {
    console.error('Missing REACT_APP_FIREBASE_VAPID_KEY environment variable')
    return false
  }

  return true
}

// Check if configuration is valid
const isValidConfig = validateFirebaseConfig()

// Firebase configuration object
const firebaseConfig: FirebaseConfig = {
  apiKey: envConfig.firebase_api_key,
  authDomain: envConfig.firebase_auth_domain,
  projectId: envConfig.firebase_project_id,
  storageBucket: envConfig.firebase_storage_bucket || `${envConfig.firebase_project_id}.firebasestorage.app`,
  messagingSenderId: envConfig.firebase_messaging_sender_id,
  appId: envConfig.firebase_app_id,
  measurementId: envConfig.firebase_measurement_id || ''
}

const vapidKey = envConfig.firebase_vapid_key

// Initialize Firebase only if config is valid
let app: any = null
let messaging: any = null

if (isValidConfig) {
  try {
    app = initializeApp(firebaseConfig)
    messaging = getMessaging(app)
  } catch (error) {
    console.error('Failed to initialize Firebase:', error)
  }
}

// Service Worker management utilities
class ServiceWorkerManager {
  private static async unregisterExistingServiceWorkers(): Promise<void> {
    if (!('serviceWorker' in navigator)) return

    const registrations = await navigator.serviceWorker.getRegistrations()
    const firebaseRegistrations = registrations.filter(
      registration => registration.scope.includes('firebase-messaging-sw.js') || registration.scope.includes('firebase')
    )

    await Promise.all(firebaseRegistrations.map(registration => registration.unregister()))

    if (firebaseRegistrations.length > 0) {
      console.log(`Unregistered ${firebaseRegistrations.length} existing Firebase service workers`)
    }
  }

  private static createServiceWorkerContent(): string {
    return `
      /* eslint-disable */
      importScripts('https://www.gstatic.com/firebasejs/8.1.0/firebase-app.js');
      importScripts('https://www.gstatic.com/firebasejs/8.1.0/firebase-messaging.js');

      const firebaseConfig = ${JSON.stringify(firebaseConfig)};

      firebase.initializeApp(firebaseConfig);
      const messaging = firebase.messaging();

      // Handle background messages when app is closed/in background
      messaging.onBackgroundMessage((payload) => {
        console.log('Received background message:', payload);

        const notificationData = payload.data || {};
        const { type, threadId } = notificationData;

        // Skip notification for lock_forced - it will show modal instead
        if (type === 'workplan_lock_forced' || type === 'process_lock_forced') {
          console.log('Skipping notification for lock_forced - modal will be shown instead');
          return;
        }

        const notificationTitle = payload.notification?.title || 'New Message!';

        // Create unique tag to prevent duplicate notifications for same thread
        const notificationTag = type && threadId ? \`\${type}_\${threadId}\` : 'howfactory-notification';

        const notificationOptions = {
          body: payload.notification?.body || 'You have a new notification',
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          data: notificationData,
          tag: notificationTag,
          requireInteraction: false,
          silent: false
        };

        return self.registration.showNotification(notificationTitle, notificationOptions);
      });

      // Handle notification click events with proper thread redirection
      self.addEventListener('notificationclick', (event) => {
        console.log('Notification clicked:', event);

        event.notification.close();

        // Get notification data for redirection
        const notificationData = event.notification.data || {};
        const { type, threadId } = notificationData;

        // Simple URL construction - same as in-app notifications
        let targetUrl = '/messages';
        if (type && threadId) {
          if (type === 'message') {
            targetUrl = \`/messages?tab=general&threadId=\${threadId}\`;
          } else if (type === 'support_message') {
            targetUrl = \`/messages?tab=support&threadId=\${threadId}\`;
          } else if (type === 'notification') {
            targetUrl = '/notifications';
          }
        }

        // Simple navigation - just open/focus the URL
        event.waitUntil(
          clients.matchAll({ type: 'window', includeUncontrolled: true }).then((clientList) => {
            // Look for existing app window
            for (const client of clientList) {
              if (client.url.includes(self.location.origin) && 'focus' in client) {
                client.navigate(targetUrl);
                return client.focus();
              }
            }

            // If no app window is open, open new one
            if (clients.openWindow) {
              return clients.openWindow(targetUrl);
            }
          })
        );
      });
    `
  }

  static async registerDynamicServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator) || !isValidConfig) {
      return
    }

    try {
      // Always use static service worker - blob URLs are unreliable
      await ServiceWorkerManager.fallbackToStaticServiceWorker()
    } catch (error) {
      throw error
    }
  }

  private static async fallbackToStaticServiceWorker(): Promise<void> {
    try {
      // Clean up existing service workers first
      await ServiceWorkerManager.unregisterExistingServiceWorkers()
      // Register service worker with root scope
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/'
      })
      // Wait for service worker to activate
      if (registration.installing) {
        await new Promise<void>(resolve => {
          registration.installing?.addEventListener('statechange', () => {
            if (registration?.installing?.state === 'activated') {
              resolve()
            }
          })
        })
      }
    } catch (fallbackError) {
      throw new Error('Firebase service worker registration failed')
    }
  }
}

// Export the service worker registration function
export const registerFirebaseServiceWorker = ServiceWorkerManager.registerDynamicServiceWorker

// Export messaging instance
export { messaging }

// FCM Token generation with improved error handling
export const generateToken = async (): Promise<string | null> => {
  if (!isValidConfig) {
    console.error('Firebase messaging not available - check configuration')
    return null
  }

  if (!messaging) {
    console.error('Firebase messaging not initialized')
    return null
  }

  try {
    // Request permission first
    const permission = await Notification.requestPermission()

    if (permission !== EFirebasePermission.GRANTED) {
      console.warn('Notification permission not granted:', permission)
      return null
    }

    // Generate token with VAPID key
    const token = await getToken(messaging, { vapidKey })

    if (!token) {
      console.error('Failed to generate FCM token - no token returned')
      return null
    }

    console.log('FCM token generated successfully')
    return token
  } catch (error) {
    console.error('Error generating FCM token:', error)
    throw error
  }
}

// Message listener with improved error handling
export async function onMessageListener(): Promise<void> {
  if (!isValidConfig) {
    console.warn('Firebase messaging not available for message listener')
    return
  }

  if (!messaging) {
    console.warn('Firebase messaging not initialized for message listener')
    return
  }

  try {
    onMessage(messaging, payload => {
      console.log('Received foreground message:', payload)

      // Skip notification for lock_forced - it will show modal instead
      const messageType = payload?.data?.type
      if (messageType === 'workplan_lock_forced' || messageType === 'process_lock_forced') {
        console.log('Skipping notification for lock_forced - modal will be shown instead')
        return
      }

      // Only show notification if app is in foreground and user granted permission
      if (Notification.permission === EFirebasePermission.GRANTED) {
        const notificationTitle = payload?.notification?.title ?? 'New Message!'
        const notificationOptions = {
          body: payload?.notification?.body ?? 'You have a new notification',
          icon: '/favicon.ico',
          tag: 'howfactory-notification'
        }

        new Notification(notificationTitle, notificationOptions)
      }
    })
  } catch (error) {
    console.error('Error setting up message listener:', error)
  }
}

// Firebase status utility
export const getFirebaseStatus = () => ({
  isConfigValid: isValidConfig,
  isInitialized: !!app && !!messaging,
  hasValidVapidKey: !!vapidKey && vapidKey.length >= 80,
  notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'unavailable',
  configDetails: {
    hasApiKey: !!envConfig.firebase_api_key,
    hasAuthDomain: !!envConfig.firebase_auth_domain,
    hasProjectId: !!envConfig.firebase_project_id,
    hasVapidKey: !!envConfig.firebase_vapid_key,
    vapidKeyLength: envConfig.firebase_vapid_key?.length || 0
  }
})

// Debug function to log Firebase status
export const debugFirebaseStatus = () => {
  const status = getFirebaseStatus()
  return status
}

// Make debug function available globally for easy troubleshooting
if (typeof window !== 'undefined') {
  ;(window as any).debugFirebaseStatus = debugFirebaseStatus
}
